import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

const CouponManagement = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    code: '',
    discount_percent: '',
    usage_limit: '',
    expiry_date: '',
    is_active: true,
  });
  const [saving, setSaving] = useState(false);

  const fetchCoupons = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error } = await supabase
        .from('coupons')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setCoupons(data || []);
    } catch (err) {
      setError('Failed to load coupons: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCoupons();
  }, [fetchCoupons]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        discount_percent: parseInt(form.discount_percent, 10),
        is_active: form.is_active,
        usage_limit: form.usage_limit ? parseInt(form.usage_limit, 10) : null,
        expiry_date: form.expiry_date || null,
      };
      const { error } = await supabase.from('coupons').insert([payload]);
      if (error) throw error;
      setForm({ code: '', discount_percent: '', usage_limit: '', expiry_date: '', is_active: true });
      setShowForm(false);
      fetchCoupons();
    } catch (err) {
      setError('Failed to create coupon: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (coupon) => {
    try {
      const { error } = await supabase
        .from('coupons')
        .update({ is_active: !coupon.is_active })
        .eq('id', coupon.id);
      if (error) throw error;
      fetchCoupons();
    } catch (err) {
      setError('Failed to update coupon: ' + err.message);
    }
  };

  const deleteCoupon = async (id) => {
    if (!window.confirm('Delete this coupon? This cannot be undone.')) return;
    try {
      const { error } = await supabase.from('coupons').delete().eq('id', id);
      if (error) throw error;
      fetchCoupons();
    } catch (err) {
      setError('Failed to delete coupon: ' + err.message);
    }
  };

  return (
    <div className="coupon-management">
      <div className="content-header">
        <h2>Coupon Management</h2>
        <button className="add-button" onClick={() => setShowForm((v) => !v)}>
          {showForm ? '✕ Cancel' : '+ Create Coupon'}
        </button>
      </div>

      {error && (
        <div style={{ background: '#fee', color: '#c33', padding: '0.75rem 1rem', borderRadius: 8, marginBottom: '1rem', borderLeft: '4px solid #c33' }}>
          {error}
        </div>
      )}

      {showForm && (
        <div className="coupon-form-card">
          <h3>New Coupon</h3>
          <form onSubmit={handleSubmit} className="coupon-form">
            <div className="form-row">
              <div className="form-group">
                <label>Coupon Code *</label>
                <input
                  type="text"
                  name="code"
                  value={form.code}
                  onChange={handleChange}
                  placeholder="e.g. SAVE20"
                  required
                />
              </div>
              <div className="form-group">
                <label>Discount % *</label>
                <input
                  type="number"
                  name="discount_percent"
                  value={form.discount_percent}
                  onChange={handleChange}
                  placeholder="e.g. 20"
                  min="1"
                  max="100"
                  required
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Usage Limit (leave blank for unlimited)</label>
                <input
                  type="number"
                  name="usage_limit"
                  value={form.usage_limit}
                  onChange={handleChange}
                  placeholder="e.g. 100"
                  min="1"
                />
              </div>
              <div className="form-group">
                <label>Expiry Date (leave blank for no expiry)</label>
                <input
                  type="date"
                  name="expiry_date"
                  value={form.expiry_date}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={form.is_active}
                  onChange={handleChange}
                />
                Active (users can apply this coupon)
              </label>
            </div>
            <button type="submit" className="submit-order-button" disabled={saving}>
              {saving ? 'Creating...' : 'Create Coupon'}
            </button>
          </form>
        </div>
      )}

      {loading ? (
        <div className="loading-spinner"><div className="spinner"></div></div>
      ) : coupons.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">🎟️</div>
          <h3>No Coupons Yet</h3>
          <p>Create your first coupon above.</p>
        </div>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Discount</th>
              <th>Used / Limit</th>
              <th>Expiry</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {coupons.map((c) => (
              <tr key={c.id}>
                <td data-label="Code"><strong>{c.code}</strong></td>
                <td data-label="Discount">{c.discount_percent}% off</td>
                <td data-label="Used / Limit">
                  {c.times_used} / {c.usage_limit ?? '∞'}
                </td>
                <td data-label="Expiry">
                  {c.expiry_date ? new Date(c.expiry_date).toLocaleDateString() : 'No expiry'}
                </td>
                <td data-label="Status">
                  <span className={`status-badge ${c.is_active ? 'completed' : 'cancelled'}`}>
                    {c.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <button className="action-btn view" onClick={() => toggleActive(c)}>
                    {c.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                  <button
                    className="action-btn"
                    style={{ marginLeft: '0.5rem', background: '#fee', color: '#c33', border: '1px solid #c33' }}
                    onClick={() => deleteCoupon(c.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default CouponManagement;
